import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Star, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Product } from '../types';
import { formatWeight, formatPurity } from '../lib/utils';

interface ProductCardProps {
  product: Product;
  onAddToCart?: (product: Product) => void;
  onToggleFavorite?: (product: Product) => void;
  onProductClick?: (product: Product) => void;
  isFavorite?: boolean;
}

export function ProductCard({
  product,
  onAddToCart,
  onToggleFavorite,
  onProductClick,
  isFavorite = false
}: ProductCardProps) {
  const discountPercentage = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(product);
    }
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite(product);
    }
  };

  const productUrl = `/product/${product.slug}`;

  return (
    <Link to={productUrl} className="block group">
      <Card
        className="overflow-hidden border-border hover:shadow-lg transition-all duration-300 hover:border-brand-green/30 cursor-pointer"
        onClick={() => onProductClick?.(product)}
      >
        <div className="relative aspect-square overflow-hidden bg-gray-50">
          <ImageWithFallback
            src={(Array.isArray(product.images) && product.images.length > 0) ? product.images[0] : ''}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />

          <div className="absolute top-2 left-2 flex flex-col gap-1">
            {product.isNew && (
              <Badge className="bg-brand-green hover:bg-brand-green/90 text-white">
                New
              </Badge>
            )}
            {product.isBestseller && (
              <Badge variant="secondary" className="bg-brand-gold/20 text-brand-gold border-brand-gold/30">
                Bestseller
              </Badge>
            )}
            {/* {discountPercentage > 0 && (
              <Badge variant="destructive">
                {discountPercentage}% OFF
              </Badge>
            )} */}
          </div>

          {onToggleFavorite && (
            <Button
              variant="ghost"
              size="icon"
              className={`
                absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity
                ${isFavorite ? 'text-red-500 opacity-100' : 'text-muted-foreground hover:text-red-500'}
              `}
              onClick={handleToggleFavorite}
            >
              <Heart className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
            </Button>
          )}

          {onAddToCart && (
            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                size="sm"
                className="bg-brand-green hover:bg-brand-green/90 text-white"
                onClick={handleAddToCart}
              >
                <ShoppingCart className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          )}
        </div>

        <CardContent className="p-4">
          <div className="space-y-2">
            <div className="flex items-start justify-between">
              <h3 className="line-clamp-2 text-sm">{product.name}</h3>
            </div>

            {(product.weight || product.purity) && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {product.weight && <span>{formatWeight(product.weight)}</span>}
                {product.weight && product.purity && <span>•</span>}
                {product.purity && <span>{formatPurity(product.purity, product.category)}</span>}
              </div>
            )}

            {/* <div className="flex items-center gap-1">
              <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
              <span className="text-xs text-muted-foreground">
                {product.rating} ({product.reviewCount})
              </span>
            </div> */}

            {/* <div className="flex items-center gap-2">
              <span className="text-brand-green">₹{product.price.toLocaleString()}</span>
              {product.originalPrice && (
                <span className="text-xs text-muted-foreground line-through">
                  ₹{product.originalPrice.toLocaleString()}
                </span>
              )}
            </div> */}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}